
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from 'uuid';
import { ExtendedMessage } from "../types/chat";
import {
  Bot,
  Zap,
  Lightbulb,
  Code,
  Settings,
  FileText,
  Calculator,
  Globe,
  Users,
  Mail,
  Calendar,
  Database,
  Briefcase,
  Target,
  type LucideIcon
} from "lucide-react";
import React from "react";
import { logger } from "@/lib/logger";
import { isAbortError } from "@/utils/errorUtils";

// Generate a smart session title based on user's first message
const generateSessionTitle = (userMessage: string): string => {
  // Clean the message
  const cleanMessage = userMessage.trim().toLowerCase();
  
  // Define conversation categories with keywords and titles
  const categories = [
    {
      keywords: ['automation', 'automate', 'workflow', 'process'],
      title: '🤖 Automation Discussion'
    },
    {
      keywords: ['website', 'web', 'site', 'landing page', 'webpage'],
      title: '🌐 Website Project'
    },
    {
      keywords: ['integration', 'connect', 'api', 'sync', 'link'],
      title: '🔗 Integration Setup'
    },
    {
      keywords: ['chatbot', 'bot', 'ai assistant', 'virtual assistant'],
      title: '🤖 Chatbot Development'
    },
    {
      keywords: ['email', 'marketing', 'campaign', 'newsletter'],
      title: '📧 Email Automation'
    },
    {
      keywords: ['data', 'database', 'analytics', 'reporting'],
      title: '📊 Data & Analytics'
    },
    {
      keywords: ['scheduling', 'calendar', 'appointment', 'booking'],
      title: '📅 Scheduling System'
    },
    {
      keywords: ['payment', 'stripe', 'checkout', 'billing'],
      title: '💳 Payment Integration'
    },
    {
      keywords: ['crm', 'customer', 'lead', 'sales'],
      title: '👥 CRM Solution'
    },
    {
      keywords: ['social media', 'facebook', 'instagram', 'twitter'],
      title: '📱 Social Media Automation'
    },
    {
      keywords: ['help', 'support', 'question', 'how to'],
      title: '❓ Support Question'
    },
    {
      keywords: ['price', 'cost', 'pricing', 'quote', 'estimate'],
      title: '💰 Pricing Inquiry'
    }
  ];

  // Check for category matches
  for (const category of categories) {
    if (category.keywords.some(keyword => cleanMessage.includes(keyword))) {
      return category.title;
    }
  }

  // Fallback: create a smart title from the first meaningful words
  const words = userMessage.split(' ').filter(word => word.length > 2);
  const meaningfulWords = words.slice(0, 3).join(' ');
  
  if (meaningfulWords.length > 5) {
    return `💬 ${meaningfulWords.charAt(0).toUpperCase() + meaningfulWords.slice(1)}`;
  }

  // Final fallback with timestamp
  const timeStamp = new Date().toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  return `💬 Chat ${timeStamp}`;
};

// Check if a session title needs updating (lacks emoji or seems generic)
export const shouldUpdateTitle = (currentTitle: string): boolean => {
  // Check if title already has emoji (smart naming)
  const hasEmoji = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(currentTitle);
  
  // Check if title is generic/basic
  const isGeneric = currentTitle.includes('New Chat') || 
                   currentTitle.includes('Chat ') || 
                   currentTitle.length < 10 ||
                   /^[A-Za-z\s]{1,20}$/.test(currentTitle.trim());
  
  return !hasEmoji || isGeneric;
};

// Re-analyze and update session title based on existing messages
export const updateSessionTitle = async (conversationId: string): Promise<string | null> => {
  try {
    // Fetch the first user message from this session
    const { data: messages, error } = await supabase
      .from('chat_messages')
      .select('content')
      .eq('conversation_id', conversationId)
      .eq('role', 'user')
      .order('created_at', { ascending: true })
      .limit(1);

    if (error || !messages || messages.length === 0) {
      logger.error('Error fetching messages for title update:', { error: error });
      return null;
    }

    const firstUserMessage = messages[0].content;
    const newTitle = generateSessionTitle(firstUserMessage);

    // Update the session title in the database
    const { error: updateError } = await supabase
      .from('chat_sessions')
      .update({ title: newTitle })
      .eq('id', conversationId);

    if (updateError) {
      logger.error('Error updating session title:', { error: updateError });
      return null;
    }

    return newTitle;
  } catch (error) {
    logger.error('Error in updateSessionTitle:', { error: error });
    return null;
  }
};

/**
 * Ensure a `chat_sessions` row exists before the brain request.
 * Call this (and sync `currentConversationIdRef`) before `sendMessage` so
 * runs are not leased under `anon-*`.
 */
export async function ensureChatSession(
  userId: string,
  currentConversationId: string | undefined,
  organizationId?: string,
  titleHint?: string,
): Promise<string | undefined> {
  if (currentConversationId) return currentConversationId;

  try {
    const { data: session, error: convError } = await supabase
      .from("chat_sessions")
      .insert([
        {
          user_id: userId,
          title: generateSessionTitle(titleHint?.trim() || "New conversation"),
          ...(organizationId ? { organization_id: organizationId } : {}),
        },
      ])
      .select("id")
      .single();

    if (convError) {
      if (!isAbortError(convError)) {
        logger.error("Error creating session:", { error: convError });
      }
      return undefined;
    }

    return session.id as string;
  } catch (error) {
    if (!isAbortError(error)) {
      logger.error("Failed to ensure chat session:", { error });
    }
    return undefined;
  }
}

// Save message to database — returns the DB-generated message ID if successful
export const saveMessage = async (
  message: ExtendedMessage,
  isAuthenticated: boolean,
  userId: string | undefined,
  currentConversationId: string | undefined,
  setCurrentConversationId: (id: string) => void,
  organizationId?: string
): Promise<string | undefined> => {
  if (!isAuthenticated || !userId || message.isTyping) return undefined;
  
  try {
    // Create a new session if none exists
    if (!currentConversationId) {
      const sessionId = await ensureChatSession(
        userId,
        undefined,
        organizationId,
        message.content,
      );
      if (!sessionId) return undefined;

      setCurrentConversationId(sessionId);
      
      // Save the message with the new session ID and return the DB row ID
      const { data: msgData, error: msgError } = await supabase
        .from('chat_messages')
        .insert([{
          conversation_id: sessionId,
          role: message.role,
          content: message.content
        }])
        .select('id')
        .single();
        
      if (msgError) {
        logger.error("Error saving message:", { error: msgError });
        return undefined;
      }
      return msgData?.id;
    } else {
      // Save message to existing session and return the DB row ID
      const { data: msgData, error: msgError } = await supabase
        .from('chat_messages')
        .insert([{
          conversation_id: currentConversationId,
          role: message.role,
          content: message.content
        }])
        .select('id')
        .single();
        
      if (msgError) {
        logger.error("Error saving message:", { error: msgError });
        return undefined;
      }
      return msgData?.id;
    }
  } catch (error) {
    logger.error("Failed to save message:", { error: error });
    return undefined;
  }
};

// Load existing session
export const loadExistingSession = async (
  isAuthenticated: boolean,
  userId: string | undefined,
  setMessages: (messages: ExtendedMessage[]) => void,
  setCurrentConversationId: (id: string | undefined) => void,
  organizationId?: string
) => {
  if (!isAuthenticated || !userId) return;
  
  // Use unique timer IDs to prevent conflicts from concurrent calls (React StrictMode, etc.)
  const callId = uuidv4().slice(0, 8);
  const _sessionTimerId = `loadSession-${callId}`;
  const _messagesTimerId = `loadMessages-${callId}`;

  try {
    const sessionMeasure = performance.now();

    // Try to find the most recent session for THIS USER
    let query = supabase
      .from('chat_sessions')
      .select('id, title, created_at')
      .eq('user_id', userId); // CRITICAL: Filter by user_id!

    // Scope to organization if provided
    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    const { data: sessions, error: convError } = await query
      .order('created_at', { ascending: false })
      .limit(1);

    if (convError) {
      logger.error("Error loading sessions:", { error: convError });
      logger.performance("loadSessionHistory", performance.now() - sessionMeasure);
      return;
    }

    // If a session exists, load its messages
    if (sessions && sessions.length > 0) {
      const sessionId = sessions[0].id;
      setCurrentConversationId(sessionId);

      const messagesMeasure = performance.now();

      const { data: messageData, error: msgError } = await supabase
        .from('chat_messages')
        .select('id, role, content, created_at') // Only select needed fields
        .eq('conversation_id', sessionId)
        .order('created_at', { ascending: true })
        .limit(100); // Limit to last 100 messages for performance

      logger.performance("loadMessages", performance.now() - messagesMeasure);

      if (msgError) {
        logger.error("Error loading messages:", { error: msgError });
        logger.performance("loadSessionHistory", performance.now() - sessionMeasure);
        return;
      }

      if (messageData && messageData.length > 0) {
        const formattedMessages: ExtendedMessage[] = messageData.map(msg => ({
          role: msg.role as "user" | "assistant",
          content: msg.content,
          id: msg.id || uuidv4() // Ensure every message has an ID
        }));

        setMessages(formattedMessages);
        logger.info(`Loaded ${formattedMessages.length} messages from session:`, { sessionId });
      }
    } else {
      logger.info('No existing sessions found for user');
    }

    logger.performance("loadSessionHistory", performance.now() - sessionMeasure);
  } catch (error) {
    logger.error("Failed to load session history:", { error: error });
  }
};

// Generate a unique message ID
export const generateMessageId = (): string => {
  return uuidv4();
};

// Helper function to generate conversation icons based on content or index
export const getConversationIcon = (title: string, index: number): React.ReactElement => {
  // Use intelligent icon selection based on conversation title keywords
  const titleLower = title.toLowerCase();

  if (titleLower.includes('automation') || titleLower.includes('workflow'))
    return React.createElement(Zap, { className: "h-5 w-5" });
  if (titleLower.includes('code') || titleLower.includes('programming'))
    return React.createElement(Code, { className: "h-5 w-5" });
  if (titleLower.includes('idea') || titleLower.includes('suggestion'))
    return React.createElement(Lightbulb, { className: "h-5 w-5" });
  if (titleLower.includes('setting') || titleLower.includes('config'))
    return React.createElement(Settings, { className: "h-5 w-5" });
  if (titleLower.includes('document') || titleLower.includes('file'))
    return React.createElement(FileText, { className: "h-5 w-5" });
  if (titleLower.includes('calculate') || titleLower.includes('math'))
    return React.createElement(Calculator, { className: "h-5 w-5" });
  if (titleLower.includes('web') || titleLower.includes('website'))
    return React.createElement(Globe, { className: "h-5 w-5" });
  if (titleLower.includes('team') || titleLower.includes('user'))
    return React.createElement(Users, { className: "h-5 w-5" });
  if (titleLower.includes('email') || titleLower.includes('mail'))
    return React.createElement(Mail, { className: "h-5 w-5" });
  if (titleLower.includes('schedule') || titleLower.includes('calendar'))
    return React.createElement(Calendar, { className: "h-5 w-5" });
  if (titleLower.includes('data') || titleLower.includes('database'))
    return React.createElement(Database, { className: "h-5 w-5" });
  if (titleLower.includes('business') || titleLower.includes('work'))
    return React.createElement(Briefcase, { className: "h-5 w-5" });
  if (titleLower.includes('goal') || titleLower.includes('target'))
    return React.createElement(Target, { className: "h-5 w-5" });

  // Fallback based on index
  const iconMap: LucideIcon[] = [
    Bot, Zap, Lightbulb, Code, Settings, FileText, Calculator,
    Globe, Users, Mail, Calendar, Database, Briefcase, Target
  ];
  const FallbackIcon = iconMap[index % iconMap.length] ?? Bot;
  return React.createElement(FallbackIcon, { className: "h-5 w-5" });
};
